import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { toast } from 'sonner';
import { 
  Mic, MicOff, Send, Calendar, Clock, CheckCircle2, 
  Loader2, AlertTriangle, CalendarCheck, Phone, User,
  ChevronRight, Sparkles, MessageCircle, TrendingUp, TrendingDown, Minus,
  ThumbsUp, ThumbsDown, Lightbulb
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Avatar expressions
const AVATAR_EXPRESSIONS = {
  greeting: '👋',
  thinking: '🤔',
  happy: '😊',
  listening: '👂',
  excited: '🎉',
  concerned: '😟',
  neutral: '🙂',
  celebrating: '🥳',
  empathetic: '🤗'
};

// Sentiment types
const SENTIMENT = {
  POSITIVE: 'positive',
  NEGATIVE: 'negative',
  NEUTRAL: 'neutral'
};

// Conversation states
const CONVO_STATE = {
  GREETING: 'greeting',
  ASK_HOW: 'ask_how',
  LISTENING: 'listening',
  PROCESSING: 'processing',
  SENTIMENT_RESPONSE: 'sentiment_response',
  ASK_OUTCOME: 'ask_outcome',
  ASK_SCHEDULE: 'ask_schedule',
  SCHEDULE_DETAILS: 'schedule_details',
  CONFIRM: 'confirm',
  DONE: 'done'
};

// Sentiment keywords for analysis
const SENTIMENT_KEYWORDS = {
  positive: [
    'interested', 'excited', 'great', 'perfect', 'love', 'amazing', 'excellent',
    'yes', 'definitely', 'sure', 'happy', 'positive', 'good', 'nice', 'wonderful',
    'ready', 'keen', 'eager', 'buy', 'purchase', 'book', 'confirm', 'deal',
    'site visit', 'wants to see', 'schedule', 'appointment', 'liked', 'impressed'
  ],
  negative: [
    'not interested', 'no', 'expensive', 'costly', 'budget', 'cant afford',
    'too much', 'decline', 'reject', 'unhappy', 'disappointed', 'bad', 'poor',
    'wrong', 'issue', 'problem', 'complaint', 'never', 'wont', 'dont want',
    'lost', 'cold', 'dead', 'ghost', 'unreachable', 'busy', 'later', 'maybe'
  ],
  neutral: [
    'thinking', 'consider', 'will see', 'let me know', 'callback', 'follow up',
    'need time', 'discuss', 'family', 'spouse', 'partner', 'check', 'compare'
  ]
};

// Speech Recognition setup
let SpeechRecognition = null;
let hasSpeechRecognition = false;
try {
  SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  hasSpeechRecognition = !!SpeechRecognition;
} catch (e) {
  console.log('Speech Recognition not available');
}

export const PostCallAvatar = ({ 
  lead, 
  callDuration, 
  pipelineStages, 
  onSave, 
  onSkip,
  aiEnabled,
  apiKey
}) => {
  const [convoState, setConvoState] = useState(CONVO_STATE.GREETING);
  const [avatarExpression, setAvatarExpression] = useState('greeting');
  const [avatarMessage, setAvatarMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userInput, setUserInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  
  // Sentiment analysis state
  const [detectedSentiment, setDetectedSentiment] = useState(null);
  const [sentimentScore, setSentimentScore] = useState(0);
  const [sentimentInsights, setSentimentInsights] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Collected data
  const [disposition, setDisposition] = useState('');
  const [selectedStage, setSelectedStage] = useState(lead?.status || '');
  const [wantsSchedule, setWantsSchedule] = useState(null);
  const [followUpType, setFollowUpType] = useState('follow_up');
  const [followUpTitle, setFollowUpTitle] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [savingEvent, setSavingEvent] = useState(false);
  
  const speechRecognitionRef = useRef(null);
  const inputRef = useRef(null);

  // Analyze sentiment from text
  const analyzeSentiment = (text) => {
    const lowerText = text.toLowerCase();
    let positiveScore = 0;
    let negativeScore = 0;
    let neutralScore = 0;
    const insights = [];

    // Check for positive keywords
    SENTIMENT_KEYWORDS.positive.forEach(keyword => {
      if (lowerText.includes(keyword)) {
        positiveScore += 2;
        if (!insights.some(i => i.type === 'positive')) {
          insights.push({ type: 'positive', keyword });
        }
      }
    });

    // Check for negative keywords
    SENTIMENT_KEYWORDS.negative.forEach(keyword => {
      if (lowerText.includes(keyword)) {
        negativeScore += 2;
        if (!insights.some(i => i.type === 'negative')) {
          insights.push({ type: 'negative', keyword });
        }
      }
    });

    // Check for neutral keywords
    SENTIMENT_KEYWORDS.neutral.forEach(keyword => {
      if (lowerText.includes(keyword)) {
        neutralScore += 1;
        if (!insights.some(i => i.type === 'neutral')) {
          insights.push({ type: 'neutral', keyword });
        }
      }
    });

    // Calculate final sentiment
    const totalScore = positiveScore - negativeScore;
    let sentiment;
    
    if (totalScore > 2) {
      sentiment = SENTIMENT.POSITIVE;
    } else if (totalScore < -2) {
      sentiment = SENTIMENT.NEGATIVE;
    } else {
      sentiment = SENTIMENT.NEUTRAL;
    }

    return {
      sentiment,
      score: totalScore,
      confidence: Math.min(100, Math.abs(totalScore) * 15 + 40),
      insights: insights.slice(0, 3)
    };
  };

  // Get sentiment-based avatar response
  const getSentimentResponse = (sentiment, leadName) => {
    switch (sentiment) {
      case SENTIMENT.POSITIVE:
        return {
          message: `This sounds really promising! 🌟 ${leadName} seems genuinely interested. I'd suggest moving them forward in the pipeline and scheduling a follow-up soon!`,
          expression: 'celebrating',
          suggestedAction: 'Schedule site visit or meeting',
          urgency: 'high'
        };
      case SENTIMENT.NEGATIVE:
        return {
          message: `I understand this one didn't go as hoped. 💭 Don't worry though - not every call converts. Let's mark the outcome and maybe plan a nurture follow-up later.`,
          expression: 'empathetic',
          suggestedAction: 'Add to nurture campaign',
          urgency: 'low'
        };
      default:
        return {
          message: `Sounds like ${leadName} needs some time to think. 🤔 That's normal! Let's schedule a follow-up to check back in a few days.`,
          expression: 'neutral',
          suggestedAction: 'Schedule follow-up call',
          urgency: 'medium'
        };
    }
  };

  // Get stage suggestion based on sentiment
  const suggestStageFromSentiment = (sentiment) => {
    switch (sentiment) {
      case SENTIMENT.POSITIVE:
        // Look for "Interested", "Qualified", or "Site Visit" stages
        return pipelineStages.find(s => 
          s.name.toLowerCase().includes('interested') ||
          s.name.toLowerCase().includes('qualified') ||
          s.name.toLowerCase().includes('site')
        ) || pipelineStages.find(s => !s.is_win_stage && !s.is_lost_stage);
      case SENTIMENT.NEGATIVE:
        // Look for "Lost" or similar stages
        return pipelineStages.find(s => s.is_lost_stage) ||
               pipelineStages.find(s => s.name.toLowerCase().includes('lost'));
      default:
        // Keep in current or move to "Contacted" stage
        return pipelineStages.find(s => 
          s.name.toLowerCase().includes('contacted') ||
          s.name.toLowerCase().includes('follow')
        ) || pipelineStages.find(s => s.slug === lead?.status);
    }
  };

  // Avatar typing effect
  const typeMessage = async (message, expression = 'neutral') => {
    setIsTyping(true);
    setAvatarExpression(expression);
    setAvatarMessage('');
    
    for (let i = 0; i <= message.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 20));
      setAvatarMessage(message.substring(0, i));
    }
    setIsTyping(false);
  };

  // Initial greeting
  useEffect(() => {
    const startConversation = async () => {
      await typeMessage(
        `Hey! Great job on that ${formatDuration(callDuration)} call with ${lead?.name}! 🎉 How did it go?`,
        'greeting'
      );
      setConvoState(CONVO_STATE.ASK_HOW);
    };
    startConversation();
  }, []);

  // Handle conversation flow
  const handleUserResponse = async (response) => {
    setUserInput('');
    
    switch (convoState) {
      case CONVO_STATE.ASK_HOW:
        setDisposition(response);
        setAvatarExpression('thinking');
        setIsAnalyzing(true);
        await typeMessage('Analyzing the call outcome...', 'thinking');
        
        // Perform sentiment analysis
        await new Promise(resolve => setTimeout(resolve, 800));
        const analysis = analyzeSentiment(response);
        setDetectedSentiment(analysis.sentiment);
        setSentimentScore(analysis.score);
        setSentimentInsights(analysis.insights);
        setIsAnalyzing(false);
        
        // Get sentiment-based response
        const sentimentResponse = getSentimentResponse(analysis.sentiment, lead?.name);
        
        // Suggest stage based on sentiment
        const suggestedStage = suggestStageFromSentiment(analysis.sentiment);
        if (suggestedStage) {
          setSelectedStage(suggestedStage.slug);
        }
        
        // Set follow-up date based on urgency
        const followUpDate = new Date();
        if (sentimentResponse.urgency === 'high') {
          followUpDate.setDate(followUpDate.getDate() + 1); // Tomorrow
        } else if (sentimentResponse.urgency === 'medium') {
          followUpDate.setDate(followUpDate.getDate() + 3); // In 3 days
        } else {
          followUpDate.setDate(followUpDate.getDate() + 7); // Next week
        }
        setSelectedDate(followUpDate.toISOString().split('T')[0]);
        
        await typeMessage(sentimentResponse.message, sentimentResponse.expression);
        setConvoState(CONVO_STATE.SENTIMENT_RESPONSE);
        break;

      case CONVO_STATE.SENTIMENT_RESPONSE:
        // User acknowledged sentiment analysis, move to stage confirmation
        const confirmedStageFromSentiment = pipelineStages.find(s => 
          s.name.toLowerCase().includes(response.toLowerCase()) ||
          response.toLowerCase().includes('yes') ||
          response.toLowerCase().includes('ok') ||
          response.toLowerCase().includes('sure') ||
          response.toLowerCase().includes('sounds good')
        );
        
        if (confirmedStageFromSentiment) {
          setSelectedStage(confirmedStageFromSentiment.slug);
        }
        
        // Suggest scheduling based on sentiment
        if (detectedSentiment === SENTIMENT.POSITIVE) {
          await typeMessage(
            `Great! Since ${lead?.name} is interested, let's schedule a follow-up soon. Want me to help find a time? 📅`,
            'excited'
          );
        } else if (detectedSentiment === SENTIMENT.NEGATIVE) {
          await typeMessage(
            `Should we schedule a follow-up call later, or mark this as complete for now?`,
            'neutral'
          );
        } else {
          await typeMessage(
            `Would you like to schedule a follow-up to check back with ${lead?.name}? 📅`,
            'happy'
          );
        }
        setConvoState(CONVO_STATE.ASK_SCHEDULE);
        break;

      case CONVO_STATE.ASK_OUTCOME:
        // User confirmed or selected a stage
        const confirmedStage = pipelineStages.find(s => 
          s.name.toLowerCase().includes(response.toLowerCase()) ||
          response.toLowerCase().includes('yes') ||
          response.toLowerCase().includes('sure')
        );
        
        if (confirmedStage) {
          setSelectedStage(confirmedStage.slug);
        }
        
        await typeMessage(
          `Perfect! Now, would you like to schedule a follow-up action? I can help you find a good time! 📅`,
          'excited'
        );
        setConvoState(CONVO_STATE.ASK_SCHEDULE);
        break;

      case CONVO_STATE.ASK_SCHEDULE:
        const wantsToSchedule = response.toLowerCase().includes('yes') || 
                               response.toLowerCase().includes('sure') ||
                               response.toLowerCase().includes('schedule') ||
                               response.toLowerCase().includes('find');
        setWantsSchedule(wantsToSchedule);
        
        if (wantsToSchedule) {
          // Set default date if not already set
          if (!selectedDate) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            setSelectedDate(tomorrow.toISOString().split('T')[0]);
          }
          setFollowUpTitle(`Follow-up with ${lead?.name}`);
          
          // Suggest follow-up type based on sentiment
          if (detectedSentiment === SENTIMENT.POSITIVE) {
            await typeMessage(
              `Since they're interested, I'd suggest a site visit or meeting. What works best?`,
              'happy'
            );
          } else {
            await typeMessage(
              `What type of follow-up would you like to schedule? A call, site visit, or meeting?`,
              'happy'
            );
          }
          setConvoState(CONVO_STATE.SCHEDULE_DETAILS);
        } else {
          await typeMessage(
            `No problem! I've noted everything down. Ready to save this call? 🙌`,
            'happy'
          );
          setConvoState(CONVO_STATE.CONFIRM);
        }
        break;

      case CONVO_STATE.SCHEDULE_DETAILS:
        // Parse follow-up type from response
        if (response.toLowerCase().includes('site') || response.toLowerCase().includes('visit')) {
          setFollowUpType('site_visit');
        } else if (response.toLowerCase().includes('meeting')) {
          setFollowUpType('meeting');
        } else {
          setFollowUpType('follow_up');
        }
        
        await typeMessage(
          `Perfect! I've set up the follow-up. Check the time slots below and pick one that works for you!`,
          'happy'
        );
        setConvoState(CONVO_STATE.CONFIRM);
        break;

      default:
        break;
    }
  };

  // Format duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs} second`;
    return `${mins} minute${mins > 1 ? 's' : ''}`;
  };

  // Fetch available slots
  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/calendar/availability?date=${date}&duration_minutes=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => {
    if (selectedDate && wantsSchedule) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, wantsSchedule]);

  // Voice input
  const startVoiceInput = () => {
    if (!hasSpeechRecognition) {
      toast.error('Voice input is not supported in this browser');
      return;
    }

    try {
      speechRecognitionRef.current = new SpeechRecognition();
      speechRecognitionRef.current.continuous = false;
      speechRecognitionRef.current.interimResults = true;
      speechRecognitionRef.current.lang = 'en-US';

      speechRecognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = 0; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setUserInput(transcript);
      };

      speechRecognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          toast.error('Voice input error: ' + event.error);
        }
        setIsRecording(false);
      };

      speechRecognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      speechRecognitionRef.current.start();
      setIsRecording(true);
      setAvatarExpression('listening');
      toast.success('Listening... speak now!');
    } catch (error) {
      console.error('Speech recognition error:', error);
      toast.error('Could not start voice input');
    }
  };

  const stopVoiceInput = () => {
    if (speechRecognitionRef.current) {
      speechRecognitionRef.current.stop();
      setIsRecording(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    setSavingEvent(true);
    try {
      const token = localStorage.getItem('token');
      
      // 1. Log the call activity
      await fetch(`${API}/api/crm/leads/${lead.id}/activities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          activity_type: 'call',
          notes: disposition,
          call_duration: callDuration,
          call_outcome: 'completed',
        }),
      });

      // 2. Update lead stage if changed
      if (selectedStage && selectedStage !== lead.status) {
        await fetch(`${API}/api/crm/leads/${lead.id}/stage`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ stage: selectedStage }),
        });
      }

      // 3. Create calendar event if scheduled
      if (wantsSchedule && selectedDate && selectedTime && followUpTitle) {
        const startTime = new Date(`${selectedDate}T${selectedTime}`);
        const endTime = new Date(startTime.getTime() + 30 * 60000);
        
        await fetch(`${API}/api/calendar/events`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            title: followUpTitle,
            description: `Follow-up from call: ${disposition.substring(0, 200)}...`,
            event_type: followUpType,
            start_time: startTime.toISOString(),
            end_time: endTime.toISOString(),
            lead_id: lead.id,
            reminder_minutes: 30
          }),
        });
        
        toast.success('Call logged and follow-up scheduled! 🎉');
      } else {
        toast.success('Call logged successfully! ✅');
      }

      onSave && onSave();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save');
    } finally {
      setSavingEvent(false);
    }
  };

  const formatTimeDisplay = (isoTime) => {
    const date = new Date(isoTime);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="space-y-4">
      {/* Avatar Section */}
      <div className="relative">
        <Card className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-amber-200 overflow-hidden">
          <CardContent className="p-4">
            {/* Avatar Character */}
            <div className="flex items-start gap-4">
              {/* Animated Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform hover:scale-105">
                  <span className="text-3xl" role="img" aria-label="avatar">
                    {AVATAR_EXPRESSIONS[avatarExpression]}
                  </span>
                </div>
                {/* Pulse animation when listening */}
                {isRecording && (
                  <div className="absolute -inset-1 bg-red-400/30 rounded-2xl animate-ping" />
                )}
                {/* Sparkles for excitement */}
                {avatarExpression === 'excited' && (
                  <Sparkles className="absolute -top-2 -right-2 w-5 h-5 text-yellow-500 animate-bounce" />
                )}
              </div>

              {/* Message Bubble */}
              <div className="flex-1 relative">
                <div className="bg-white rounded-2xl rounded-tl-sm p-4 shadow-sm border border-amber-100">
                  <p className="text-gray-800 leading-relaxed">
                    {avatarMessage}
                    {isTyping && <span className="animate-pulse">|</span>}
                  </p>
                </div>
                {/* Triangle pointer */}
                <div className="absolute top-4 -left-2 w-0 h-0 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-white" />
              </div>
            </div>

            {/* Sentiment Analysis Display */}
            {detectedSentiment && !isAnalyzing && (convoState === CONVO_STATE.SENTIMENT_RESPONSE || convoState === CONVO_STATE.ASK_SCHEDULE || convoState === CONVO_STATE.SCHEDULE_DETAILS || convoState === CONVO_STATE.CONFIRM) && (
              <div className="mt-4 p-3 rounded-xl bg-white/80 border border-amber-100" data-testid="sentiment-analysis-card">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-amber-600" />
                    <span className="text-sm font-medium text-gray-700">AI Sentiment Analysis</span>
                  </div>
                  <Badge 
                    variant="outline"
                    className={`text-xs ${
                      detectedSentiment === SENTIMENT.POSITIVE 
                        ? 'bg-green-50 text-green-700 border-green-200' 
                        : detectedSentiment === SENTIMENT.NEGATIVE 
                        ? 'bg-red-50 text-red-700 border-red-200' 
                        : 'bg-gray-50 text-gray-700 border-gray-200'
                    }`}
                    data-testid="sentiment-badge"
                  >
                    {detectedSentiment === SENTIMENT.POSITIVE && <TrendingUp className="h-3 w-3 mr-1" />}
                    {detectedSentiment === SENTIMENT.NEGATIVE && <TrendingDown className="h-3 w-3 mr-1" />}
                    {detectedSentiment === SENTIMENT.NEUTRAL && <Minus className="h-3 w-3 mr-1" />}
                    {detectedSentiment.charAt(0).toUpperCase() + detectedSentiment.slice(1)}
                  </Badge>
                </div>
                
                {/* Sentiment Insights */}
                {sentimentInsights.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {sentimentInsights.map((insight, idx) => (
                      <Badge 
                        key={idx} 
                        variant="secondary"
                        className={`text-xs ${
                          insight.type === 'positive' ? 'bg-green-100 text-green-800' :
                          insight.type === 'negative' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {insight.type === 'positive' && <ThumbsUp className="h-2.5 w-2.5 mr-1" />}
                        {insight.type === 'negative' && <ThumbsDown className="h-2.5 w-2.5 mr-1" />}
                        "{insight.keyword}"
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Suggested Stage */}
                {selectedStage && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                    <span>Suggested stage:</span>
                    <Badge 
                      variant="outline"
                      style={{ backgroundColor: pipelineStages.find(s => s.slug === selectedStage)?.color + '20' }}
                    >
                      {pipelineStages.find(s => s.slug === selectedStage)?.name}
                    </Badge>
                  </div>
                )}
              </div>
            )}

            {/* Analyzing Indicator */}
            {isAnalyzing && (
              <div className="mt-4 flex items-center gap-2 p-3 rounded-xl bg-white/80 border border-amber-100">
                <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
                <span className="text-sm text-gray-600">Analyzing call sentiment...</span>
              </div>
            )}

            {/* Quick Response Buttons - Sentiment Response State */}
            {!isTyping && convoState === CONVO_STATE.SENTIMENT_RESPONSE && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Badge 
                  variant="outline" 
                  className="cursor-pointer hover:bg-green-100 transition-colors px-3 py-1.5 border-green-200"
                  onClick={() => handleUserResponse('sounds good')}
                  data-testid="btn-accept-sentiment"
                >
                  <ThumbsUp className="h-3 w-3 mr-1" />
                  Sounds good!
                </Badge>
                <Badge 
                  variant="outline" 
                  className="cursor-pointer hover:bg-amber-100 transition-colors px-3 py-1.5"
                  onClick={() => handleUserResponse('change stage')}
                >
                  Change stage
                </Badge>
                {pipelineStages.slice(0, 3).map(stage => (
                  <Badge 
                    key={stage.slug}
                    variant="outline" 
                    className="cursor-pointer hover:bg-gray-100 transition-colors px-3 py-1.5"
                    onClick={() => {
                      setSelectedStage(stage.slug);
                      handleUserResponse(stage.name);
                    }}
                  >
                    {stage.name}
                  </Badge>
                ))}
              </div>
            )}

            {/* Quick Response Buttons - Show based on state */}
            {!isTyping && convoState === CONVO_STATE.ASK_OUTCOME && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Badge 
                  variant="outline" 
                  className="cursor-pointer hover:bg-amber-100 transition-colors px-3 py-1.5"
                  onClick={() => handleUserResponse('yes')}
                >
                  Yes, looks good!
                </Badge>
                {pipelineStages.slice(0, 4).map(stage => (
                  <Badge 
                    key={stage.slug}
                    variant="outline" 
                    className="cursor-pointer hover:bg-amber-100 transition-colors px-3 py-1.5"
                    onClick={() => {
                      setSelectedStage(stage.slug);
                      handleUserResponse(stage.name);
                    }}
                  >
                    {stage.name}
                  </Badge>
                ))}
              </div>
            )}

            {!isTyping && convoState === CONVO_STATE.ASK_SCHEDULE && (
              <div className="mt-4 flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white hover:bg-green-50 border-green-200"
                  onClick={() => handleUserResponse('yes')}
                >
                  <CalendarCheck className="w-4 h-4 mr-1" />
                  Yes, schedule
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white hover:bg-gray-50"
                  onClick={() => handleUserResponse('no')}
                >
                  Skip for now
                </Button>
              </div>
            )}

            {!isTyping && convoState === CONVO_STATE.SCHEDULE_DETAILS && (
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white hover:bg-blue-50 border-blue-200"
                  onClick={() => handleUserResponse('call')}
                >
                  <Phone className="w-4 h-4 mr-1" />
                  Follow-up Call
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white hover:bg-green-50 border-green-200"
                  onClick={() => handleUserResponse('site visit')}
                >
                  <User className="w-4 h-4 mr-1" />
                  Site Visit
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="bg-white hover:bg-purple-50 border-purple-200"
                  onClick={() => handleUserResponse('meeting')}
                >
                  <MessageCircle className="w-4 h-4 mr-1" />
                  Meeting
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* User Input Section - For ASK_HOW state */}
      {convoState === CONVO_STATE.ASK_HOW && !isTyping && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                ref={inputRef}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Tell me how the call went..."
                className="pr-12"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && userInput.trim()) {
                    handleUserResponse(userInput);
                  }
                }}
                data-testid="avatar-input"
              />
              {hasSpeechRecognition && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={`absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 ${isRecording ? 'text-red-500' : 'text-gray-400'}`}
                  onClick={isRecording ? stopVoiceInput : startVoiceInput}
                  data-testid="avatar-voice-btn"
                >
                  {isRecording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
              )}
            </div>
            <Button 
              onClick={() => userInput.trim() && handleUserResponse(userInput)}
              disabled={!userInput.trim()}
              data-testid="avatar-send-btn"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Quick suggestion chips */}
          <div className="flex flex-wrap gap-2">
            {['Interested in 2BHK', 'Wants site visit', 'Discussing budget', 'Not interested'].map(suggestion => (
              <Badge 
                key={suggestion}
                variant="outline"
                className="cursor-pointer hover:bg-gray-100 transition-colors text-xs"
                onClick={() => {
                  setUserInput(suggestion);
                  inputRef.current?.focus();
                }}
              >
                {suggestion}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Scheduling Section - Show when in CONFIRM state and wants schedule */}
      {convoState === CONVO_STATE.CONFIRM && wantsSchedule && (
        <Card className="border-amber-200">
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-amber-600" />
              <span className="font-medium">Schedule Follow-up</span>
            </div>

            {/* Follow-up Title */}
            <div>
              <Input
                value={followUpTitle}
                onChange={(e) => setFollowUpTitle(e.target.value)}
                placeholder="Follow-up title"
                data-testid="avatar-followup-title"
              />
            </div>

            {/* Date Selection */}
            <div>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="avatar-date-input"
              />
            </div>

            {/* Time Slots */}
            <div>
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-1">
                <Clock className="h-4 w-4" />
                Available slots
              </p>
              {loadingSlots ? (
                <div className="flex justify-center py-2">
                  <Loader2 className="h-5 w-5 animate-spin text-amber-500" />
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                  {availableSlots.map((slot, index) => (
                    <Button
                      key={index}
                      variant={selectedTime === slot.start_time.split('T')[1]?.substring(0, 5) ? "default" : "outline"}
                      size="sm"
                      className={`text-xs ${!slot.is_available ? 'opacity-40 line-through' : ''}`}
                      disabled={!slot.is_available}
                      onClick={() => setSelectedTime(slot.start_time.split('T')[1]?.substring(0, 5))}
                    >
                      {formatTimeDisplay(slot.start_time)}
                    </Button>
                  ))}
                </div>
              )}
            </div>

            {/* Selected time confirmation */}
            {selectedTime && (
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg">
                <CalendarCheck className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700">
                  {new Date(`${selectedDate}T${selectedTime}`).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  })} at {formatTimeDisplay(`${selectedDate}T${selectedTime}`)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Card - Show in CONFIRM state */}
      {convoState === CONVO_STATE.CONFIRM && !isTyping && (
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Ready to save
            </p>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Call Duration:</span>
                <span className="font-medium">{formatDuration(callDuration)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">New Stage:</span>
                <Badge style={{ backgroundColor: pipelineStages.find(s => s.slug === selectedStage)?.color }}>
                  {pipelineStages.find(s => s.slug === selectedStage)?.name || selectedStage}
                </Badge>
              </div>
              {disposition && (
                <div>
                  <span className="text-muted-foreground">Notes:</span>
                  <p className="text-xs mt-1 p-2 bg-white rounded border">
                    {disposition.substring(0, 100)}{disposition.length > 100 ? '...' : ''}
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {convoState === CONVO_STATE.CONFIRM && !isTyping && (
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onSkip}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={savingEvent || !disposition}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            data-testid="avatar-save-btn"
          >
            {savingEvent ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-1" />
            )}
            Save Call
          </Button>
        </div>
      )}
    </div>
  );
};

export default PostCallAvatar;
